import React, { useEffect, useMemo, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import Panel from '@adsk/alloy-react-panel';
import {
  useLocale,
  displayDate,
  VALID_DATE_FORMATS,
} from '@adsk/alloy-react-helpers';
import Divider from '@adsk/alloy-react-divider';
import theme from '@adsk/alloy-react-theme';
import InlineEdit, { InlineTextAreaInput } from '@adsk/alloy-react-inline-edit';
import { useCurrentAccountId } from '@adsk/alloy-provider';
import { useDataApi } from '@adsk/alloy-helpers';
import {
  useNotifications,
  NOTIFICATION_STATUSES,
} from '@adsk/alloy-react-notification';
import { ActionButton } from '@adsk/alloy-react-menu';
import { PencilIcon, TrashCanIcon } from '@adsk/alloy-react-icon';
import { withFormik } from 'formik';
import { COMPONENT_VARIANTS } from '@common/constants';
import { getTrades, isEmptyObject } from '@common/utils';
import SelectBox from '@adsk/alloy-select-box';
import Header from './layout/Header';
import EditAddress from './layout/EditAddress';
import ViewAddress from './layout/ViewAddress';
import {
  dividerStyles,
  fieldStyles,
  labelStyles,
  getInlineEditStyle,
} from './styles';
import { buildUpdateAccountCompanyRequest } from './utils';
import { FIELDS_NAME_MAP_FOR_EVENT } from './constants';

export const CompanyProfilePanel = ({
  company,
  mapBoxAccessToken,
  onSave,
  variant,
  ...rest
}) => {
  const { T, locale: localeName } = useLocale(({ locale }) =>
    require(`./i18n/${locale}.json`)
  );
  const trades = useMemo(() => getTrades(localeName), [localeName]);
  const {
    data: updateCompanyData,
    error: updateCompanyError,
    doFetch: doFetchUpdateCompany,
  } = useDataApi();
  const accountId = useCurrentAccountId();
  const { showNotification } = useNotifications();
  const locationOptions = useMemo(
    () => [
      {
        key: 'edit',
        label: T.translate('EDIT_LOCATION'),
        icon: PencilIcon,
      },
      {
        key: 'remove',
        label: T.translate('REMOVE_LOCATION'),
        icon: TrashCanIcon,
      },
    ],
    [T]
  );
  const lastUpdatedField = useRef();
  const isAccountVariant = variant === COMPONENT_VARIANTS.ACCOUNT;
  const { setFieldValue, values } = rest;
  const tradeValueForSelect =
    values.trade && values.trade !== ''
      ? {
          value: values.trade,
          label:
            trades.find((t) => t.value === values.trade)?.label || values.trade,
        }
      : null;
  const {
    category,
    createdAt,
    description,
    erpId,
    id,
    imageUrl,
    name,
    projectSize,
    taxId,
    trade,
    updatedAt,
    userSize,
    websiteUrl,
  } = company || {};

  const updateCompany = useCallback(
    (field, value) => {
      let body = {
        [field]: value,
      };
      if (field === 'addresses' && value.length === 0) {
        body = {
          addresses: [
            {
              addressLine1: '',
              addressLine2: '',
              city: '',
              country: null,
              phone: '',
              postalCode: '',
              stateOrProvince: null,
              type: 'Main',
            },
          ],
        };
      }
      setFieldValue(field, value);
      lastUpdatedField.current = FIELDS_NAME_MAP_FOR_EVENT[field];
      doFetchUpdateCompany(
        ...buildUpdateAccountCompanyRequest(accountId, id, body)
      );
    },
    [setFieldValue, doFetchUpdateCompany, accountId, id]
  );

  const handleAddAddressClick = (unsavedAddress, unsavedAddressIndex) => {
    let newAddresses;
    if (values.addresses.length === 0) {
      newAddresses = [unsavedAddress, {}];
    } else {
      newAddresses = [...values.addresses, {}];
      newAddresses[unsavedAddressIndex] = unsavedAddress;
    }
    setFieldValue('addresses', newAddresses);
    setFieldValue('activeEditLocation', newAddresses.length - 1);
  };

  const handleSaveAddress = (unsavedAddress, unsavedAddressIndex) => {
    const newAddresses = [...values.addresses];
    newAddresses[unsavedAddressIndex] = unsavedAddress;
    setFieldValue('activeEditLocation', null);
    updateCompany('addresses', newAddresses);
  };

  const handleEditExitAddress = (currentValue, unsavedAddressIndex) => {
    setFieldValue('activeEditField', null);
    if (values.addresses.length > 0) {
      if (isEmptyObject(values.addresses[unsavedAddressIndex])) {
        const newAddresses = values.addresses.filter((value, index) => {
          return unsavedAddressIndex !== index;
        });
        setFieldValue('addresses', newAddresses);
      }
      setFieldValue('activeEditLocation', null);
    }
  };

  const handleActionButtonChange = (selectedIndex, option) => {
    if (option === 'remove') {
      const newAddresses = values.addresses.filter((value, index) => {
        return selectedIndex !== index;
      });
      updateCompany('addresses', newAddresses);
    } else if (option === 'edit') {
      setFieldValue('activeEditLocation', selectedIndex);
    }
  };

  const customHeader = useCallback(
    ({ onClose }) => {
      return (
        <Header
          imageUrl={imageUrl}
          isAccountVariant={isAccountVariant}
          name={name}
          onClose={onClose}
          owner={category === 'owner'}
          updateCompany={updateCompany}
          websiteUrl={websiteUrl}
        />
      );
    },
    [category, imageUrl, isAccountVariant, name, updateCompany, websiteUrl]
  );

  const checkEmptyAddress = () => {
    const { type: addressType, ...restAddress } = values.addresses[0];

    return isEmptyObject(restAddress);
  };

  useEffect(() => {
    if (!updateCompanyData) return;

    showNotification({
      status: NOTIFICATION_STATUSES.SUCCESS,
      message: T.translate('CHANGES_SAVED', {
        companyName: name,
      }),
    });

    if (onSave) onSave(lastUpdatedField.current, updateCompanyData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateCompanyData]);

  useEffect(() => {
    if (!updateCompanyError) return;

    showNotification({
      status: NOTIFICATION_STATUSES.ERROR,
      message: T.translate('CHANGES_NOT_SAVED', {
        companyName: name,
      }),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateCompanyError]);

  if (!company || values?.id == null) return null;

  return (
    <Panel {...rest} renderHeader={customHeader}>
      <Panel.Body key={values.id}>
        <div css={fieldStyles}>
          <div css={labelStyles}>{T.translate('TRADE')}</div>
          <InlineEdit
            disabled={!isAccountVariant}
            onChange={(value) => updateCompany('trade', value?.value || '')}
            onEditEnter={() => setFieldValue('activeEditField', 'trade')}
            onEditExit={() => setFieldValue('activeEditField', null)}
            placeholder={T.translate('TRADE_PLACEHOLDER')}
            renderInput={(passedProps) => (
              <SelectBox
                clearable
                defaultIsOpen
                searchable
                maxMenuHeight={180}
                options={trades}
                value={{ value: values.trade }}
                {...passedProps}
              />
            )}
            renderValue={(value) => value.label}
            showPencilOnHoverOnly
            style={getInlineEditStyle(
              isAccountVariant,
              trade == null || trade === '',
              values.activeEditField === 'trade'
            )}
            value={tradeValueForSelect}
          />
        </div>
        <div css={fieldStyles}>
          <div css={labelStyles}>{T.translate('ERP')}</div>
          <InlineEdit
            data-testid="CompanyProfilePanel_inlineEdit_erpId"
            disabled={!isAccountVariant}
            onChange={(value) => updateCompany('erpId', value)}
            onEditEnter={() => setFieldValue('activeEditField', 'erpId')}
            onEditExit={() => setFieldValue('activeEditField', null)}
            placeholder={T.translate('ERP_PLACEHOLDER')}
            showPencilOnHoverOnly
            style={getInlineEditStyle(
              isAccountVariant,
              erpId == null || erpId === '',
              values.activeEditField === 'erpId'
            )}
            value={values.erpId}
          />
        </div>
        <div css={fieldStyles}>
          <div css={labelStyles}>{T.translate('TAX_ID')}</div>
          <InlineEdit
            disabled={!isAccountVariant}
            onChange={(value) => updateCompany('taxId', value)}
            onEditEnter={() => setFieldValue('activeEditField', 'taxId')}
            onEditExit={() => setFieldValue('activeEditField', null)}
            placeholder={T.translate('TAX_ID_PLACEHOLDER')}
            showPencilOnHoverOnly
            style={getInlineEditStyle(
              isAccountVariant,
              taxId == null || taxId === '',
              values.activeEditField === 'taxId'
            )}
            value={values.taxId}
          />
        </div>
        <div css={fieldStyles}>
          <div css={labelStyles}>{T.translate('ADDED_ON')}</div>
          <div>
            {displayDate(new Date(createdAt), {
              format: VALID_DATE_FORMATS.SHORT,
            })}
          </div>
        </div>
        <div css={fieldStyles}>
          <div css={labelStyles}>{T.translate('LAST_UPDATED')}</div>
          <div>
            {displayDate(new Date(updatedAt), {
              format: VALID_DATE_FORMATS.SHORT,
            })}
          </div>
        </div>
        <div>
          <div css={theme.typography.heading3}>{T.translate('LOCATION')}</div>
          <Divider style={dividerStyles} />
          {values.addresses.length === 0 || checkEmptyAddress() === true ? (
            <div css={fieldStyles}>
              <InlineEdit
                data-testid="CompanyProfilePanel_inlineEdit_address_0"
                disabled={!isAccountVariant}
                onChange={(newValue) => handleSaveAddress(newValue, 0)}
                onEditEnter={() =>
                  setFieldValue('activeEditField', 'addresses')
                }
                onEditExit={() => handleEditExitAddress({}, 0)}
                placeholder={T.translate('ADD_ADDRESS')}
                renderInput={(passedProps) => (
                  <EditAddress
                    index={0}
                    mapBoxAccessToken={mapBoxAccessToken}
                    onAddAddressClick={handleAddAddressClick}
                    {...passedProps}
                  />
                )}
                showPencilOnHoverOnly
                style={{
                  ...getInlineEditStyle(
                    isAccountVariant,
                    true,
                    values.activeEditField === 'addresses'
                  ),
                  border: 'none',
                }}
              />
            </div>
          ) : (
            values.addresses.map((address, index) => (
              // eslint-disable-next-line react/no-array-index-key
              <div css={fieldStyles} key={index}>
                {values.activeEditLocation === index ? (
                  <EditAddress
                    address={address}
                    index={index}
                    mapBoxAccessToken={mapBoxAccessToken}
                    onAddAddressClick={handleAddAddressClick}
                    onChange={handleSaveAddress}
                    onClose={handleEditExitAddress}
                  />
                ) : (
                  <div css={{ display: 'flex', columnGap: '20px' }}>
                    <ViewAddress address={address} />
                    {isAccountVariant && (
                      <ActionButton
                        data-testid={`CompanyProfilePanel_address_ActionButton_${index}`}
                        horizontal={false}
                        onChange={([option]) =>
                          handleActionButtonChange(index, option)
                        }
                        options={locationOptions}
                        style={{ marginLeft: 'auto', border: 'none' }}
                      />
                    )}
                  </div>
                )}
              </div>
            ))
          )}
        </div>
        <div>
          <div css={theme.typography.heading3}>{T.translate('OTHER')}</div>
          <Divider style={dividerStyles} />
          <div css={fieldStyles}>
            <div css={labelStyles}>{T.translate('DESCRIPTION')}</div>
            <InlineEdit
              disabled={!isAccountVariant}
              onChange={(value) => updateCompany('description', value)}
              onEditEnter={() =>
                setFieldValue('activeEditField', 'description')
              }
              onEditExit={() => setFieldValue('activeEditField', null)}
              placeholder={T.translate('DESCRIPTION_PLACEHOLDER')}
              renderInput={({ onChange: _, ...passedProps }) => {
                return <InlineTextAreaInput {...passedProps} />;
              }}
              showPencilOnHoverOnly
              style={{
                whiteSpace: 'pre-wrap',
                ...getInlineEditStyle(
                  isAccountVariant,
                  description == null || description === '',
                  values.activeEditField === 'description'
                ),
              }}
              value={values.description}
            />
          </div>
        </div>
        {isAccountVariant && (
          <>
            <div css={fieldStyles}>
              <div css={labelStyles}>{T.translate('MEMBERS')}</div>
              <div>{T.translate('VIEW_ALL_MEMBERS', { userSize })}</div>
            </div>
            <div css={fieldStyles}>
              <div css={labelStyles}>{T.translate('PROJECTS')}</div>
              <div>{T.translate('PROJECTS_NUMBER', { projectSize })}</div>
            </div>
          </>
        )}
      </Panel.Body>
    </Panel>
  );
};

CompanyProfilePanel.propTypes = {
  company: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    trade: PropTypes.string,
    addresses: PropTypes.arrayOf(PropTypes.object),
    websiteUrl: PropTypes.string,
    description: PropTypes.string,
    erpId: PropTypes.string,
    taxId: PropTypes.string,
    imageUrl: PropTypes.string,
    createdAt: PropTypes.string,
    updatedAt: PropTypes.string,
    projectSize: PropTypes.number,
    userSize: PropTypes.number,
  }),
  mapBoxAccessToken: PropTypes.string.isRequired,
  onSave: PropTypes.func,
  variant: PropTypes.oneOf(Object.values(COMPONENT_VARIANTS)),
};

CompanyProfilePanel.defaultProps = {
  company: null,
  onSave: null,
  variant: COMPONENT_VARIANTS.PROJECT,
};

const CompanyProfilePanelWithFormik = withFormik({
  enableReinitialize: true,
  mapPropsToValues: (props) => {
    const {
      addresses = [],
      description,
      erpId,
      id,
      name,
      taxId,
      trade,
      websiteUrl,
    } = props.company || {};

    return {
      activeEditLocation: null,
      activeEditField: null,
      addresses: addresses.map((address) => ({
        ...address,
        addressLine1: address.addressLine1 || '',
        addressLine2: address.addressLine2 || '',
        city: address.city || '',
        postalCode: address.postalCode || '',
        phone: address.phone || '',
      })) || [{}],
      description,
      erpId,
      id,
      name,
      taxId,
      trade,
      websiteUrl,
    };
  },
  displayName: 'CompanyProfilePanel',
})(CompanyProfilePanel);

export default CompanyProfilePanelWithFormik;
